// =============================================================================
// MeuDriver — Hook do relatório mensal do instrutor (I4)
// =============================================================================

import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import { startOfMonth, endOfMonth, parseISO } from 'date-fns'

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

export interface ReportBooking {
  id: string
  scheduled_at: string
  duration_minutes: number
  total_price: number | null
  instructor_payout: number | null
  student_name: string | null
}

export interface ReportReview {
  id: string
  rating: number
  comment: string | null
  created_at: string
  student_name: string | null
}

export interface InstructorReportData {
  month: string              // 'YYYY-MM'
  instructorName: string | null
  totalLessons: number
  totalRevenue: number       // soma instructor_payout
  avgRating: number | null
  bookings: ReportBooking[]
  reviews: ReportReview[]
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useInstructorReport(month: string) {
  const { user } = useAuth()

  return useQuery<InstructorReportData, Error>({
    queryKey: ['instructor-report', month, user?.id],
    queryFn: async () => {
      if (!user?.id) throw new Error('Usuário não autenticado')

      // Resolve instructor_id
      const { data: instRow, error: instErr } = await supabase
        .from('instructors')
        .select('id, profile:profiles(full_name)')
        .eq('profile_id', user.id)
        .single()
      if (instErr) throw new Error(instErr.message)

      const instructorId = (instRow as any).id as string
      const instructorName = (instRow as any).profile?.full_name ?? null

      // Intervalo do mês
      const [year, mon] = month.split('-').map(Number)
      const refDate = new Date(year, mon - 1, 1)
      const mStart = startOfMonth(refDate).toISOString()
      const mEnd = endOfMonth(refDate).toISOString()

      // Busca bookings concluídos no mês
      const { data: bookingsRaw, error: bErr } = await supabase
        .from('bookings')
        .select('id, scheduled_at, duration_minutes, total_price, instructor_payout, student:profiles!bookings_student_id_fkey(full_name)')
        .eq('instructor_id', instructorId)
        .eq('status', 'completed')
        .gte('scheduled_at', mStart)
        .lte('scheduled_at', mEnd)
        .order('scheduled_at', { ascending: true })
      if (bErr) throw new Error(bErr.message)

      // Busca reviews recebidas no mês
      const { data: reviewsRaw, error: rErr } = await supabase
        .from('reviews')
        .select('id, rating, comment, created_at, student:profiles!reviews_student_id_fkey(full_name)')
        .eq('instructor_id', instructorId)
        .gte('created_at', mStart)
        .lte('created_at', mEnd)
        .order('created_at', { ascending: false })
      if (rErr) throw new Error(rErr.message)

      const bookings: ReportBooking[] = (bookingsRaw ?? []).map((b: any) => ({
        id: b.id,
        scheduled_at: b.scheduled_at,
        duration_minutes: b.duration_minutes,
        total_price: b.total_price,
        instructor_payout: b.instructor_payout,
        student_name: b.student?.full_name ?? null,
      }))

      const reviews: ReportReview[] = (reviewsRaw ?? []).map((r: any) => ({
        id: r.id,
        rating: r.rating,
        comment: r.comment,
        created_at: r.created_at,
        student_name: r.student?.full_name ?? null,
      }))

      const totalRevenue = bookings.reduce((s, b) => s + (b.instructor_payout ?? 0), 0)
      const avgRating = reviews.length
        ? reviews.reduce((s, r) => s + r.rating, 0) / reviews.length
        : null

      return {
        month,
        instructorName,
        totalLessons: bookings.length,
        totalRevenue,
        avgRating,
        bookings,
        reviews,
      }
    },
    enabled: Boolean(user?.id) && Boolean(month),
    staleTime: 1000 * 60 * 5,
  })
}
